import dayjs from 'dayjs';
import useEventDetails from 'hooks/useEventDetails';
import sumBy from 'lodash/sumBy';
import useWorkshopDetails from './useWorkshopDetails';

interface UseWorkshopStatsOption {
  publisherSlug: string;
  productId: string;
}

const useWorkshopStats = ({
  publisherSlug,
  productId
}: UseWorkshopStatsOption) => {
  const {
    workshopDetails,
    isLoading: isWorkshopLoading,
    error: workshopError,
    publisherDetails
  } = useWorkshopDetails({
    publisherSlug,
    productId
  });
  const {
    eventDetails,
    isLoading: isEventLoading,
    error: eventError
  } = useEventDetails(workshopDetails?.event_id ?? '');
  const isLoading = isWorkshopLoading || isEventLoading;
  const error = workshopError || eventError;
  const totalPrice = sumBy(workshopDetails?.prices, ({ price }) => price ?? 0);
  const totalSeats = workshopDetails?.maximum_number_of_attendees ?? 0;
  const occupiedSeats = eventDetails?.number_of_attendees ?? 0;
  const emptySeats = totalSeats - occupiedSeats;
  const hasSeatAvailable = emptySeats > 0;
  const currency = workshopDetails?.prices?.[0]?.currency ?? '';
  const { _next_occurrences } = eventDetails ?? {};
  const isWorkshopInProgress =
    !eventDetails?.is_finished &&
    dayjs(_next_occurrences?.[0]?.start_date?.epoch).isBefore(
      dayjs().valueOf()
    );

  return {
    isLoading, // Overall loading status
    isWorkshopLoading,
    isEventLoading,
    workshopDetails,
    publisherDetails,
    eventDetails,
    occupiedSeats,
    emptySeats,
    totalPrice,
    currency,
    hasSeatAvailable,
    error,
    isPaidWorkshop: totalPrice > 0,
    isWorkshopInProgress,
    isCancelled: workshopDetails?.status === 'CANCELLED',
    isDeleted: workshopDetails?.status === 'DELETED',
    isPending: workshopDetails?.status === 'PENDING'
  };
};

export default useWorkshopStats;
